import { useRouter, computed, ref } from '@nuxtjs/composition-api';
import { useKarteFavoriteTag } from '~/composables/karte/useKarteFavoriteTag';
import { useAlert } from '~/composables/useAlert';
import { usePropSync } from '~/composables/usePropSync';
import { useStore } from '~/composables/useStore';
import { url } from '~/utils';
import { FavoriteType } from '~/types/common/user';
import type { ArticleCategory } from '~/types/modules/contents';
import { FavoriteTypes } from '~/enums';

export const useFavoriteButton = (props: {
  active: boolean;
  type: FavoriteType;
  favoriteCode: string;
  categories?: ArticleCategory[];
}) => {
  const router = useRouter();
  const store = useStore();
  const karteFavoriteTag = useKarteFavoriteTag();
  const { openAlert } = useAlert();
  const { syncedActive } = usePropSync(props, 'active');
  const authenticated = computed(() => store.state.auth.authenticated);

  const isProcessing = ref(false); // 連打対策用のフラグ
  const clicked = ref(false);

  const openLoginDialog = () => {
    openAlert({
      title: 'ログインしてください',
      message: 'お気に入りをご利用するにはログインしてください',
      buttons: [
        { text: 'キャンセル', style: 'cancel' },
        {
          text: 'ログイン',
          style: 'submit',
          async onClick() {
            try {
              await router.push({ path: url('AUTH_LOGIN') });
            } catch {
              return false;
            }
          },
        },
      ],
    });
  };

  const onClickFavoriteButton = async () => {
    // 未ログインの場合はアラートを表示
    if (!authenticated.value) {
      openLoginDialog();
      return;
    }

    if (isProcessing.value) return;

    isProcessing.value = true;

    // お気に入りの登録・削除処理
    if (syncedActive.value) {
      await store.dispatch('user/favorites/deleteFavorite', {
        favoriteType: props.type,
        favoriteCode: props.favoriteCode,
      });
    } else {
      await store.dispatch('user/favorites/addFavorite', {
        favoriteType: props.type,
        favoriteCode: props.favoriteCode,
      });
    }

    syncedActive.value = !syncedActive.value;

    // KARTEとSalesForceの送信
    switch (props.type) {
      case FavoriteTypes.ITEM:
        karteFavoriteTag.setKarteFavoriteProductsTag(props.favoriteCode, syncedActive.value);
        try {
          // collect_code
          if (window._etmc) {
            window._etmc.push(['trackPageView', { item: 'f_' + props.favoriteCode }]);
          }
        } catch {
          // 何もしない
        }
        break;
      case FavoriteTypes.ARTICLE:
        karteFavoriteTag.setKarteFavoriteMagazineTag(
          parseInt(props.favoriteCode),
          syncedActive.value,
          props.categories || []
        );
        break;
      case FavoriteTypes.PRODUCER:
        karteFavoriteTag.setKarteFavoriteProducersTag(parseInt(props.favoriteCode), syncedActive.value);
        break;
    }

    isProcessing.value = false;
    clicked.value = true;
  };

  return {
    onClickFavoriteButton,
    syncedActive,
    clicked,
  };
};
